import { useCallback, useEffect } from 'react'
import { create } from 'zustand'
import { useStorageState } from './useStorageState'

// ///////////////////////
// MARK: TYPES
interface IChromeCollapseStore {
  collapsed: boolean
}

// ///////////////////////
// MARK: STORE
const useChromeCollapseStore = create<IChromeCollapseStore>(() => ({
  collapsed: false,
}))

// ///////////////////////
// MARK: EXPORTS
export function useChromeCollapsed(): boolean {
  return useChromeCollapseStore((state) => state.collapsed)
}

// Réservé aux pages plein écran : la préférence n'est appliquée que tant que la page
// est montée, et la navigation est systématiquement rendue au démontage — aucun écran
// ne doit pouvoir laisser l'utilisateur sans barre de navigation.
// autoCollapse : repli proposé par la page tant que l'utilisateur n'a jamais choisi lui-même.
export function useCollapsibleChrome(
  enabled = true,
  autoCollapse?: boolean,
): [collapsed: boolean, toggle: () => void] {
  const [stored, setStored] = useStorageState<boolean>('local', 'chrome-collapsed', false)
  const [chosen, setChosen] = useStorageState<boolean>('local', 'chrome-collapsed-chosen', false)

  // Un repli enregistré avant l'option automatique vaut choix.
  const manual = chosen || stored || autoCollapse === undefined
  const collapsed = enabled && (manual ? stored : autoCollapse)

  useEffect(() => {
    useChromeCollapseStore.setState({ collapsed })

    return () => useChromeCollapseStore.setState({ collapsed: false })
  }, [collapsed])

  // Seul un choix fait sur une page à repli automatique désactive ce repli.
  const toggle = useCallback(() => {
    if (autoCollapse !== undefined) setChosen(true)
    setStored(!collapsed)
  }, [autoCollapse, collapsed, setChosen, setStored])

  return [collapsed, toggle]
}
